import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { initialClientEmailState, initialState } from './initialState';
import { ApiExError } from '@shared';

interface PostIntakeFormEmailResponseType {
  success: boolean;
  result: any;
  error?: string;
}

const clientEmailSlice = createSlice({
  name: 'clientEmail',
  initialState: initialState.clientEmail,
  reducers: {
    postIntakeFormEmailRequest(state, action: PayloadAction<IntakeFormEmailProps>) {
      if (action.payload.isTestEmail) {
        state.status.testEmail = 'loading';
      } else {
        state.status.individualIntakeForm = 'loading';
      }
    },
    postIntakeFormEmailResponse(state, _action: PayloadAction<PostIntakeFormEmailResponseType>) {
      const payload = _action.payload;
      if (state.status.testEmail === 'loading') {
        state.status.testEmail = 'complete';
      } else {
        state.status.individualIntakeForm = 'complete';
      }

      if (!payload?.success) {
        state.errorMessage = payload?.error || 'Bad Request';
        state.status.individualIntakeForm = 'error';
      }
    },
    postIntakeFormEmailFailure(state, action: PayloadAction<ApiExError>) {
      if (state.status.testEmail === 'loading') {
        state.status.testEmail = 'error';
      } else {
        state.status.individualIntakeForm = 'error';
      }
    },
    resetClientEmailState(state) {
      const { status, errorMessage, partialSuccessMessage, failedEmails } = initialClientEmailState;
      state.status = status;
      state.errorMessage = errorMessage;
      state.partialSuccessMessage = partialSuccessMessage;
      state.failedEmails = failedEmails;
    },
    postBatchIntakeFormEmailRequest(state, action: PayloadAction<BatchIntakeFormEmailProps>) {
      state.errorMessage = null;
      state.partialSuccessMessage = null;
      state.status.batchIntakeForm = 'loading';
    },
    postBatchIntakeFormEmailResponse(state, action: PayloadAction<BatchEmailReminderResult>) {
      const { errorCount, successCount, errors } = action.payload;
      if (successCount > 0 && errorCount > 0) {
        state.status.batchIntakeForm = 'completeWithErrors';
        state.errorMessage = `The following emails failed to send: ${errors
          .map(error => error.email)
          .join(', ')}. Please try again or contact support if the issue persists.`;
        state.failedEmails = errors.map(error => error.email);
        state.partialSuccessMessage = `Successfully sent ${successCount} invites. `;
      } else if (successCount > 0) {
        state.status.batchIntakeForm = 'complete';
      } else {
        state.status.batchIntakeForm = 'error';
        state.errorMessage = 'Error sending invites';
      }
    },
    postBatchIntakeFormEmailFailure(state, action: PayloadAction<ApiExError>) {
      state.status.batchIntakeForm = 'error';
    },
  },
});

export const {
  postIntakeFormEmailRequest,
  postIntakeFormEmailResponse,
  postIntakeFormEmailFailure,
  resetClientEmailState,
  postBatchIntakeFormEmailRequest,
  postBatchIntakeFormEmailResponse,
  postBatchIntakeFormEmailFailure,
} = clientEmailSlice.actions;

export const clientEmailSliceReducer = clientEmailSlice.reducer;
